import './App.scss';

import { Amplify } from 'aws-amplify';
import { LoadingOverlay, Snackbar, SubNavigation } from '@finpay-development/shared-components';
import { CircularProgress, Grid, Box, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import jwt from 'jwt-decode';
import React, {useEffect, useState, useRef, useCallback} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from 'react-router-dom';

import {
  setIsPublicMode,
  setPathName,
  setUrlParameters,
} from './admin/state/users/admin-slice';
import { setIsChannel, setChannelType } from './guest/state/guest-slice';
import { PublicPagesWrapper } from './guest/components/PublicPagesWrapper';
import { isCurrentUrlPublic } from './guest/misc/helpers';
import RouteConfiguration, { RouteConfigProps } from './route-configuration';
import AuthenticationMonitor from './security/components/authentication-monitor';
import GuardedComponent from './security/components/guarded-component';
import SessionTimeout from './security/components/session-timeout';
import { hideStatus } from './security/state/user-slice';
import { checkIfUserIsAuthenticated, logUserOut, refreshToken } from './security/state/user-thunk';
import FppFooter from './shared/components/footer';
import FppHeader from './shared/components/header';
import { getConfiguration } from './shared/configuration/config-settings';
import { DecodedIdToken } from './shared/model/decodedIdToken';
import { RootState } from './shared/state/root-reducer';
import { AppDispatch } from './shared/state/store';
import FinDigitalPlatformFooter from './shared/components/findigital-platform-footer';
import {clearEncounter} from './patient/state/patient-slice';
import { cookieDomain } from './security/services/cookie-domain';
import { CookieStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import {
  UnauthenticatedAuthRouter
} from './security/components/unauthenticated-auth-router';
import AccessControl, {checkPagePermission} from './security/components/access-control';
import {
  rejectURLAccessForReadOnly,
  subNavItemsToIgnore,
} from './shared/constants/sub-nav-constants';
import { PaymentHubButton } from '@finpay-development/shared-platform/lib/esm/payment-hub/components/payment-hub-button';
import { usePaymentChannelSync } from '@finpay-development/shared-platform/lib/esm/payment-hub/hooks/use-payment-channel-sync';
import {
  specialistClientService
} from './implementation-specialist/services/implementation-client-service';
import {RolePageNames} from './security/model/role-page-names';

// get the configuration based on current url dns name
const config = getConfiguration();


// Setup Amplify AWS User Pool Configuration
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.aws_userPoolId,
      userPoolClientId: config.aws_userPoolWebClientId,
      identityPoolId: ""
    },
  },
});

const isLocalhost = window.location.hostname.includes('localhost');

function PaymentChannelSynchronizer() {
  usePaymentChannelSync();
  return null;
}

cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage({
  domain: cookieDomain,
  secure: !isLocalhost,
  expires: 1,
  sameSite: isLocalhost ? 'lax' : 'none'
}));

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const existingPatientLoginMessage_1 = "Great News!"
  const existingPatientLoginMessage_2 = "Based on the information you provided, we see you are already a FinPay patient with an active payment plan.";
  const existingPatientLoginMessage_3 = "Please login below to access your account in the FinPay Patient Portal";

  const isPublic = isCurrentUrlPublic(window.location.href);

  if (isPublic.isCurrentUrlPublic) {
    dispatch(setIsPublicMode(isPublic.isCurrentUrlPublic));
  }
  if (isPublic.channelStatus) {
    dispatch(setUrlParameters(isPublic.guestInfo));
    dispatch(setChannelType(isPublic.channelType));
    dispatch(setIsChannel(isPublic.channelStatus));
  }
  const [isMobile, setIsMobile] = useState(false)
  const hasCheckedUser = useRef(false);

  const handleResize = () => {
    if (window.innerWidth < 960) {
        setIsMobile(true)
    } else {
        setIsMobile(false)
    }
  }

  const stateFields = {
    userIsAuthenticated: useSelector((state: RootState) => state.userContext.userIsAuthenticated),
    userRolePages: useSelector((state: RootState) => state.userContext.userProfile.userRole.userRolePages),
    subNav: useSelector((state: RootState) => state.userContext.userProfile.SubNav),
    isAccountHolderUser: useSelector((state: RootState) => state.userContext.userProfile.userRole.roleName === "Account Holder"),
    applicationStatus: useSelector((state: RootState) => state.userContext.applicationStatus),
    isLoading: useSelector((state: RootState) => state.userContext.isLoading),
    idToken: useSelector((state: RootState) => state.userContext.userProfile.tokenState.jwtIdToken),
    isPublicMode: useSelector((state: RootState) => state.adminContext.adminUserContext.isPublicMode),
    isPublicModeLoggedIn: useSelector((state: RootState) => state.adminContext.adminUserContext.isPublicModeLoggedIn),
    guestContext: useSelector((state: RootState) => state.guestContext),
    isSSO: useSelector((state: RootState) => state.userContext.isSSO),
    isSSOLoading: useSelector((state: RootState) => state.userContext.isSSOLoading),
    channelType: useSelector((state: RootState) => state.guestContext.channelType),
    isChannel: useSelector((state: RootState) => state.guestContext?.isChannel),
    showLogin: useSelector((state: RootState) => state.adminContext.adminUserContext.showLogin),
    pathName: useSelector((state: RootState) => state.adminContext.adminUserContext.pathName),
    allClients: useSelector((state: RootState) => state.implementationContext?.implementationSpecialistClient?.allClients || []),
    isLoadingClients: useSelector((state: RootState) => state.implementationContext?.implementationSpecialistClient?.isLoading || false),
    allowedClients: useSelector((state: RootState) => state.userContext.userProfile.allowedClients || []),
    userId: useSelector((state: RootState) => state.userContext.userProfile.userId),
    isInitialAuthCheckComplete: useSelector((state: RootState) => state.userContext.isInitialAuthCheckComplete),
    userProfile: useSelector((state: RootState) => {
      return state.userContext.userProfile;
    }),
  }

  const {
    userIsAuthenticated,
    userRolePages,
    subNav,
    isAccountHolderUser,
    applicationStatus,
    isLoading,
    idToken,
    isPublicMode,
    isPublicModeLoggedIn,
    guestContext,
    isSSO,
    isSSOLoading,
    channelType,
    isChannel,
    showLogin,
    pathName,
    isInitialAuthCheckComplete,
    userProfile
  } = stateFields;


  useEffect(() => {
    const path = window.location.pathname
    if(path.includes('forgot-password')){
      dispatch(logUserOut());
      dispatch(setPathName('forgot-password'))
    }else if(path.includes('webpayment')){
      dispatch(setPathName('webpayment'))
    }else if(path.includes('payment-program-enrollment')){
      dispatch(setPathName('payment-program-enrollment'))
    }else if(path.includes('payment-agreement')){
      dispatch(setPathName('payment-agreement'))
    }else if(path.includes('phi-consent')){
      dispatch(setPathName('phi-consent'))
    }else if(path.includes('vob-webform-demo')){
      dispatch(setPathName('vob-webform-demo'))
    }
  }, [window.location.pathname]);


  // though the token is automatically refreshed when it expires and an api call happens,
  // this function may have some performance benefits due to caching.
  useEffect(() => {
    if (idToken) {
      const tokenInfo: DecodedIdToken = jwt(idToken);
      const timeoutMs = tokenInfo.exp * 1000 - new Date().getTime() + 500;
      setTimeout(() => {
        dispatch(refreshToken());
      }, timeoutMs);
    }
  }, [idToken, dispatch]);

  // Set user_id in Google Analytics from JWT token
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      const MEASUREMENT_ID = "G-N0S7JDQJJX"
      if (userIsAuthenticated && idToken) {
        try {
          // TODO: fix DecodedToken type
          
          const tokenInfo: any = jwt(idToken);
          const userId = `${tokenInfo.userId}-${tokenInfo.allowedClients}-${tokenInfo.email}`;
          // const userId = String(tokenInfo.userId);
          if (userId) {
            (window as any).gtag('set', {'user_id': userId});

            // Send (or re-send) page_view with user_id attached
            (window as any).gtag('config', MEASUREMENT_ID, {
              user_id: userId,
              page_path: window.location.pathname,
            });
          }
        } catch (error) {
          console.error('Error setting Google Analytics user_id:', error);
        }
      } else if (!userIsAuthenticated) {
        // Clear user_id when user logs out
        // (window as any).gtag('set', {'user_id': null});
        (window as any).gtag('config', MEASUREMENT_ID, {
          user_id: null,
          page_path: window.location.pathname,
        });
      }
    }
  }, [userIsAuthenticated, idToken]);

  useEffect(() => { handleResize() }, []);
  useEffect(() => {
    window.addEventListener("resize", handleResize)
  });

  const routeComponents = (
    <Routes>
      {RouteConfiguration.map((route: RouteConfigProps) => {
        return (
            route.protectedPageName ? (
                <Route
                    path={route.path}
                    key={route.path}
                    element={
                      <GuardedComponent
                          exact={true}
                          key={route.path}
                          path={route.path}
                          component={route.component}
                          isAuthenticated={userIsAuthenticated}
                          hasAccess={checkPagePermission({
                                userRolePages,
                                rolePageName: route.protectedPageName!,
                                permitReadOnly: !rejectURLAccessForReadOnly[route.protectedPageName]
                              }
                          )}
                      />
                    }
                />
            ) : (
                <Route
                    key={route.path}
                    path={route.path}
                    element={<route.component/>}
                />
            )
        )
      })}
    </Routes>
  );

  // use in case the promise is empty string, to prevent errors.
  const routeComponentsWithoutStripe = (
    <Routes>
      {RouteConfiguration.map((route: RouteConfigProps) => (
        <Route
          key={route.path}
          path={route.path}
          element={<route.component/>}
        />
      ))}
    </Routes>
  );
  function handleCloseSnackbar() {
    dispatch(hideStatus());
  }

  const handleLogoutClick = useCallback(async () => {
    await dispatch(logUserOut());
    window.location.reload();
  }, [dispatch]);

  const shouldShowNav = () => {
    return (
      userIsAuthenticated &&
      !window.location.pathname.includes("sign-document") &&
      !window.location.pathname.includes("sign-doc")
    ); // client signing URL
  };

  useEffect(() => {
    if (!hasCheckedUser.current && !(userIsAuthenticated && isSSO)) {
      dispatch(checkIfUserIsAuthenticated());
    }
    if (userIsAuthenticated && isSSO) {
        hasCheckedUser.current = true;
    }
  }, [dispatch, userIsAuthenticated, isSSO]);

  function toggleLoading(comp : JSX.Element){
    if (isSSOLoading || !isInitialAuthCheckComplete) {
      return <LoadingOverlay whiteBackground={true}/>;
    }
    if (isPublicMode && !userIsAuthenticated && isChannel) {
      return <LoadingOverlay whiteBackground={true}/>;
    }
    return comp;
  }

  const [showHubButton, setShowHubButton] = useState(false)

  useEffect(() => {
    if (!userIsAuthenticated) {
      setShowHubButton(false);
      return;
    }

    const clientsToCheck = userProfile.allowedClients?.filter(
      (client) => client.clientId > 0,
    ) ?? [];

    if (!clientsToCheck.length) {
      setShowHubButton(false);
      return;
    }

    (async () => {
      const promiseResponse = await Promise.all(
        clientsToCheck.map((client) =>
          specialistClientService.getClient(client.clientId),
        ),
      );
      for (let i = 0; i < promiseResponse.length; i++) {
        if (promiseResponse[i]?.entity?.fullClient?.isHubEnabled) {
          setShowHubButton(true);
          return;
        }
      }
      setShowHubButton(false);
    })();
  }, [userProfile, userIsAuthenticated]);

  const theme = useTheme();
  // Mount only one hub button so Cypress / DOM never see mobile + desktop duplicates.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const isNotAuthenticated =(<UnauthenticatedAuthRouter/>);

  const isAuthenticated = (
    <>
      <AuthenticationMonitor />
      <div className="page-container">
        <FppHeader
          showLinks={shouldShowNav()}
          paymentHub={!isDesktop && showHubButton ? (
            <AccessControl
                rolePageName={RolePageNames.PaymentHub}
                actionName=""
                strategy={{ type: 'pageOnlyWithEdit' }}
                renderNoAccess={() => null}
            >
              <PaymentHubButton variant="inline" channel='fms' />
            </AccessControl>
          ) : undefined}
        />
        {isLoading || isSSOLoading ? (
            <LoadingOverlay />
        ) : (
          <>
            {shouldShowNav() && (
              <Box sx={{ 
                width: '100%',
                paddingBottom: '4px',
                overflow: 'visible',
                position: 'relative'
              }}>
                <Box sx={{ 
                  width: '100%',
                  backgroundColor: 'white',
                  borderBottom: '3px solid rgba(0, 0, 0, 0.15)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  zIndex: 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  // TODO(sub-nav border): SubNavigation (shared-components) draws
                  // its own 3px bottom border, duplicating the strip's border
                  // above. In this flex layout the inner border would only span
                  // the tabs, so it is hidden here. Simplify by removing the
                  // border from SubNavigation in shared-components so the strip
                  // is the single border owner, then delete this override.
                  '& nav.sub-navigation': { borderBottom: 'none' },
                }}>
                  <SubNavigation
                    subNav={subNav}
                    subNavItemsToIgnore={subNavItemsToIgnore}
                    onNavClick={() => {
                      dispatch(clearEncounter());
                      // Adding v3 clears to sub-nav
                    }}
                  />
                  {/* Desktop only — mobile mounts the same button next to the hamburger. */}
                  {isDesktop && showHubButton && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      pr: '2rem',
                      '@media (min-width: 960px)': { pr: '7rem' },
                    }}>
                      <AccessControl
                          rolePageName={RolePageNames.PaymentHub}
                          actionName=""
                          strategy={{ type: 'pageOnlyWithEdit' }}
                          renderNoAccess={() => null}
                      >
                        <PaymentHubButton variant="inline" />
                      </AccessControl>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            { /* prevents stripe initialization errors caused by empty string passed into 'stripe' prop */}
            <div className="app-scroll-region">
              {config.stripe_key ? (routeComponents) : (routeComponentsWithoutStripe)}
            </div>
            {isAccountHolderUser && <div><FppFooter isMobile={isMobile} /></div>}
          </>
        )}
      </div>
    </>
  );

  return (
    <main>
    <div
      className={ channelType === 'mobile' ? "page-container mobile-page-container" : "page-container"}
      style={ channelType === 'mobile' || ['payment-program-enrollment', 'payment-agreement', 'phi-consent', 'vob-webform-demo'].includes(pathName) ? { backgroundColor: "#FFF", height: "initial" } : { backgroundColor: "#F5F7F9" }}
    >
      <Router>
        <PaymentChannelSynchronizer />
        <Grid container>
          {!userIsAuthenticated && (
            <>
            <Grid item xs={12}>
              <FppHeader showLinks={shouldShowNav()} />
            </Grid>
            </>
          )}
          {isPublicMode && !userIsAuthenticated && !isPublicModeLoggedIn && !isChannel && pathName === 'webpayment' && (
            <Grid container direction="row" style={{ backgroundColor: "#F5F7F9"}}>
              <Grid item direction="column" md={12} xs={12} style={{textAlign: "center", padding: "2em"}}>
                { (guestContext?.isExistingPatient === undefined) && (
                  <h1>
                    {!guestContext?.bannerText && (<CircularProgress />)}
                    {guestContext.bannerText}
                  </h1>
                )}
                { (guestContext?.isExistingPatient === true) && (
                  <div>
                    <h1>{existingPatientLoginMessage_1}</h1>
                    <h1>{existingPatientLoginMessage_2}</h1>
                    <h1>{existingPatientLoginMessage_3}</h1>
                  </div>
                )}
              </Grid>
            </Grid>
          )}

          {isPublicMode && !userIsAuthenticated  && (guestContext?.isExistingPatient !== true) && !showLogin && (
                <Grid item md={!isPublicModeLoggedIn ? 5 : 12} xs={12} style={ channelType === 'mobile' ? { backgroundColor: "#FFF" } : { backgroundColor: "#F5F7F9" }}>
                  {!isPublicModeLoggedIn  && guestContext?.bannerText && !isChannel &&(
                      <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center" className={'pt-5'}>
                        <Grid item direction="column" md={12} xs={12} style={{ paddingLeft: "3%"}}>
                          Not already setup in the FinPay Patient Portal? Continue below.
                        </Grid>
                      </Grid>
                  )}
                  <div style={ channelType === 'mobile' || ['payment-program-enrollment', 'payment-agreement', 'phi-consent', 'vob-webform-demo'].includes(pathName) ? { backgroundColor: "#FFF" } : { backgroundColor: "#F5F7F9" }}>
                    <PublicPagesWrapper />
                  </div>
                </Grid>
          )}

          {/*  OR box  */}
          {isPublicMode && !userIsAuthenticated  && !isPublicModeLoggedIn && guestContext?.bannerText && !isChannel && (guestContext?.isExistingPatient === undefined) && pathName === 'webpayment' && (
            <Grid
              item
              md={2}
              xs={12}
              direction="column"
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "rgb(245,247,249)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  width: "80px",
                  height: "80px",
                  border: "3px solid #000",
                  marginTop: isMobile ? "20px" : "200px",
                  marginBottom: isMobile ? "20px" : "300px",
                }}
              >
                OR
              </div>
              <FinDigitalPlatformFooter showEmailFooter={false} channelType={channelType} />
            </Grid>
          )}

          {!isPublicModeLoggedIn &&
              (guestContext?.isExistingPatient !== false) &&
              pathName !== 'forgot-password' &&
              !['payment-program-enrollment', 'payment-agreement', 'phi-consent', 'vob-webform-demo'].includes(pathName) &&
              !(
                  window.location.pathname.includes('payment-program-enrollment') ||
                  window.location.pathname.includes('payment-agreement') ||
                  window.location.pathname.includes('phi-consent') ||
                  window.location.pathname.includes('vob-webform-demo')
              ) && (
          <Grid item md={isPublicMode && !userIsAuthenticated && !isChannel && (guestContext?.isExistingPatient === undefined) ? 5 : 12} xs={12}>
            {toggleLoading(userIsAuthenticated ? isAuthenticated : isNotAuthenticated)}
              <Snackbar
                open={applicationStatus.showMessage}
                message={applicationStatus.message}
                type={applicationStatus.messageType}
                onClose={handleCloseSnackbar}
              />
              <SessionTimeout
                isAuthenticated={userIsAuthenticated}
                logOut={handleLogoutClick}
              />
          </Grid>
          )}

        </Grid>
      </Router>
    </div>
    </main>
  );
}
export default App;
